import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { AssemblyRound, AssemblySitting } from '../../api/schema'
import { Can } from '../../contexts/PermissionsContext'
import { useAssemblyPreparation } from '../../hooks/useBallots'
import { useFormatters } from '../../lib/format'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { apiErrorMessage } from './assembly-api'
import { HelpHint } from './HelpHint'
import { NextAction, StepList, type StepDef } from './StepList'
import { downloadEvidence, useContestants, useSitting, useSittingMutations } from './sitting-api'

type Sitting = AssemblySitting

/** The participation line: who takes part, never a threshold of validity. */
export function ParticipationLine({ sitting }: { sitting: Sitting }) {
    const { t } = useTranslation('assemblies')
    const p = sitting.participation
    return (
        <p className="text-sm" data-testid="sitting-participation">
            {t('sitting.participation', {
                present: p.present_members ?? 0,
                voices: (p.present_voices ?? 0) + (p.represented_voices ?? 0),
                mail: p.mail_vote_voices ?? 0,
                total: p.total_voices ?? 0,
                members: p.total_members ?? 0,
            })}
        </p>
    )
}

/** The voices of a closed scrutin. */
export function RoundResult({ round }: { round: AssemblyRound }) {
    const { t } = useTranslation('assemblies')
    const r = (round.result ?? {}) as Record<string, number | string | undefined>
    if (round.status !== 'closed') return null
    return (
        <p className="text-xs text-muted-foreground" data-testid={`round-result-${round.number}`}>
            {r.outcome ? <strong className="mr-1">{t(`outcome.${r.outcome}`, { defaultValue: String(r.outcome) })}</strong> : null}
            {t('sitting.voices', { for: r.voices_for ?? 0, against: r.voices_against ?? 0, abstain: r.voices_abstain ?? 0, defaulting: r.voices_defaulting ?? 0 })}
        </p>
    )
}

/** A signature drawn with a finger or a mouse, read as a PNG data URL. */
function SignaturePad({ onChange }: { onChange: (png: string | null) => void }) {
    const ref = useRef<HTMLCanvasElement>(null)
    const drawing = useRef(false)
    const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        return [e.clientX - rect.left, e.clientY - rect.top] as const
    }
    return (
        <div className="space-y-1">
            <canvas
                ref={ref}
                width={360}
                height={120}
                className="w-full touch-none rounded-md border border-input bg-background"
                data-testid="sitting-signature-pad"
                onPointerDown={(e) => {
                    drawing.current = true
                    const ctx = ref.current?.getContext('2d')
                    const [x, y] = point(e)
                    ctx?.beginPath()
                    ctx?.moveTo(x, y)
                }}
                onPointerMove={(e) => {
                    if (!drawing.current) return
                    const ctx = ref.current?.getContext('2d')
                    if (!ctx) return
                    const [x, y] = point(e)
                    ctx.lineWidth = 2
                    ctx.lineTo(x, y)
                    ctx.stroke()
                }}
                onPointerUp={() => {
                    drawing.current = false
                    onChange(ref.current?.toDataURL('image/png') ?? null)
                }}
            />
        </div>
    )
}

/**
 * The console of the meeting (spec 2026-10-05-09): hold, attendance, bureau,
 * scrutins, incidents, then the minutes, their signature and the closing.
 */
export function SittingTab({ org, minutesUid, status }: { org: string; minutesUid: string; status: string }) {
    const { t } = useTranslation('assemblies')
    const { formatDate } = useFormatters()
    const held = status === 'in_progress' || status === 'closed'
    const { data: sitting } = useSitting(org, minutesUid, held)
    const { data: contestants } = useContestants(org, minutesUid, status === 'closed')
    const { data: prep } = useAssemblyPreparation(org, minutesUid)
    const m = useSittingMutations()
    const [confirmHold, setConfirmHold] = useState(false)
    const [checkInFor, setCheckInFor] = useState<string | null>(null)
    const [signature, setSignature] = useState<string | null>(null)
    const [chair, setChair] = useState('')
    const [scrutineers, setScrutineers] = useState<string[]>([])
    const [clerkFor, setClerkFor] = useState<AssemblyRound | null>(null)
    const [clerkVotes, setClerkVotes] = useState<Record<string, string>>({})
    const [clerkScrutineer, setClerkScrutineer] = useState('')
    const [amendFor, setAmendFor] = useState<AssemblyRound | null>(null)
    const [amendText, setAmendText] = useState('')
    const [amendSubstantial, setAmendSubstantial] = useState(false)
    const [reason, setReason] = useState('')
    // null = follow the work; undefined = every step closed.
    const [chosen, setChosen] = useState<string | undefined | null>(null)
    const path = { orgCode: org, contractUid: minutesUid }
    const error = apiErrorMessage(
        m.hold.error || m.checkIn.error || m.departure.error || m.bureau.error || m.open.error || m.closeVote.error ||
            m.clerkVotes.error || m.amend.error || m.suspend.error || m.resume.error || m.minutes.error || m.close.error,
    )
    const fmt = (d?: string | null) => (d ? formatDate(d, { dateStyle: 'short', timeStyle: 'short' }) : '—')

    if (!held) {
        return (
            <Card data-testid="sitting-tab">
                <CardHeader>
                    <CardTitle className="text-base">{t('sitting.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <p className="text-muted-foreground">{status === 'open' ? t('sitting.ready') : t('sitting.not_convened')}</p>
                    {status === 'open' && (
                        <Can perm="contracts.write">
                            <Button onClick={() => setConfirmHold(true)} data-testid="sitting-hold">{t('sitting.hold')}</Button>
                        </Can>
                    )}
                    {error && <p className="text-destructive" data-testid="sitting-error">{error}</p>}
                    <AlertDialog open={confirmHold} onOpenChange={setConfirmHold}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('sitting.hold_confirm_title')}</AlertDialogTitle>
                                <AlertDialogDescription data-testid="sitting-hold-confirm-body">
                                    {t('sitting.hold_confirm_body', { date: prep?.held_at ? formatDate(prep.held_at, { dateStyle: 'long' }) : '—' })}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                <AlertDialogAction data-testid="sitting-hold-confirm" onClick={() => m.hold.mutate({ params: { path } })}>
                                    {t('sitting.hold')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </CardContent>
            </Card>
        )
    }
    if (!sitting) return <p className="text-sm text-muted-foreground">{t('loading')}</p>

    const running = sitting.sitting?.status === 'running'
    const suspended = sitting.sitting?.status === 'suspended'
    const live = status === 'in_progress'
    // The sheet and the bureau are read-only once the sitting has ended (SITTING_ENDED).
    const editable = live && sitting.sitting?.status !== 'ended'
    const coOwners = sitting.attendees.filter((a) => !a.syndic)
    const present = sitting.attendees.filter((a) => a.present)
    const anyOpen = sitting.rounds.some((r) => r.status === 'open')
    const allDone = !anyOpen
    const resPath = (uid: string) => ({ params: { path: { orgCode: org, contractUid: uid } } })

    const roundsDone = sitting.rounds.filter((r) => r.status !== 'pending' && r.status !== 'open').length
    const votingStarted = sitting.rounds.some((r) => r.status !== 'pending')
    const nextPending = sitting.rounds.find((r) => r.status === 'pending')
    const openRound = sitting.rounds.find((r) => r.status === 'open')
    const signatureDone = sitting.signature?.status === 'completed'
    const chairSet = !!sitting.bureau.chair

    const steps: StepDef[] = [
        {
            id: 'attendance',
            title: t('sitting.step.attendance'),
            status: present.length > 0 && chairSet ? 'done' : 'todo',
            summary: t('sitting.step.attendance_summary', { present: present.length, total: coOwners.length, chair: sitting.bureau.chair?.name ?? '—' }),
            content: (
                <div className="space-y-4">
                    <div data-testid="sitting-bureau" className="space-y-3 text-sm">

                    <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                        <dt className="text-muted-foreground">{t('sitting.chair')}</dt>
                        <dd data-testid="sitting-chair">{sitting.bureau.chair?.name ?? '—'}</dd>
                        <dt className="text-muted-foreground">{t('sitting.secretary')}</dt>
                        <dd data-testid="sitting-secretary">{sitting.bureau.secretary?.name ?? '—'}</dd>
                        <dt className="text-muted-foreground">{t('sitting.scrutineers')}</dt>
                        <dd>{sitting.bureau.scrutineers.map((s) => s.name).join(', ') || '—'}</dd>
                    </dl>
                    {editable && (
                        <Can perm="contracts.write">
                            <div className="flex flex-wrap items-end gap-2">
                                <label className="space-y-1">
                                    <span className="block text-xs text-muted-foreground">{t('sitting.chair')}</span>
                                    <select className="h-9 rounded-md border border-input bg-background px-2" value={chair}
                                        onChange={(e) => setChair(e.target.value)} data-testid="sitting-chair-select">
                                        <option value="">—</option>
                                        {coOwners.map((a) => <option key={a.user_uid} value={a.user_uid}>{a.name}</option>)}
                                    </select>
                                </label>
                                <label className="space-y-1">
                                    <span className="block text-xs text-muted-foreground">{t('sitting.scrutineers')}</span>
                                    <select multiple className="h-20 rounded-md border border-input bg-background px-2" value={scrutineers}
                                        onChange={(e) => setScrutineers(Array.from(e.target.selectedOptions).map((o) => o.value))}
                                        data-testid="sitting-scrutineer-select">
                                        {coOwners.map((a) => <option key={a.user_uid} value={a.user_uid}>{a.name}</option>)}
                                    </select>
                                </label>
                                <Button size="sm" disabled={!chair} data-testid="sitting-bureau-save"
                                    onClick={() => m.bureau.mutate({ params: { path }, body: { chair_user_uid: chair, scrutineer_user_uids: scrutineers } })}>
                                    {t('sitting.bureau_save')}
                                </Button>
                            </div>
                        </Can>
                    )}
                    </div>
                    <div data-testid="sitting-attendance">
                    <ul className="divide-y text-sm">
                        {sitting.attendees.map((a) => (
                            <li key={a.stakeholder_uid} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`sitting-attendee-${a.user_uid}`}>
                                <span>
                                    {a.name} · {t('sitting.voices_short', { count: a.voices ?? 0 })}
                                    {a.present && (
                                        <Badge variant="outline" className="ml-2" data-testid={`sitting-present-${a.user_uid}`}>
                                            {a.represented_by ? t('sitting.represented') : t('sitting.present')} {fmt(a.since)}
                                        </Badge>
                                    )}
                                    {!a.present && a.mail_vote && <Badge variant="outline" className="ml-2">{t('sitting.mail_vote')}</Badge>}
                                </span>
                                {editable && (
                                    <Can perm="contracts.write">
                                        {a.present ? (
                                            <Button size="sm" variant="outline" data-testid={`sitting-depart-${a.user_uid}`}
                                                onClick={() => m.departure.mutate({ params: { path }, body: { stakeholder_uid: a.stakeholder_uid! } })}>
                                                {t('sitting.depart')}
                                            </Button>
                                        ) : (
                                            <Button size="sm" variant="outline" data-testid={`sitting-checkin-${a.user_uid}`}
                                                onClick={() => { setSignature(null); setCheckInFor(a.stakeholder_uid!) }}>
                                                {t('sitting.check_in')}
                                            </Button>
                                        )}
                                    </Can>
                                )}
                            </li>
                        ))}
                    </ul>
                    </div>
                </div>
            ),
        },
        {
            id: 'rounds',
            title: t('sitting.step.rounds'),
            status: present.length === 0 ? 'blocked' : sitting.rounds.length > 0 && roundsDone === sitting.rounds.length ? 'done' : 'todo',
            summary: t('sitting.step.rounds_summary', { done: roundsDone, total: sitting.rounds.length }),
            content: (
                <div className="space-y-4">
                    <div data-testid="sitting-rounds">
                    <ol className="space-y-3 text-sm">
                        {sitting.rounds.map((r) => (
                            <li key={r.resolution_uid} className="rounded-md border p-3" data-testid={`round-${r.number}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-medium">{r.number}. {r.title}{r.majority_rule ? ` (${r.majority_rule})` : ''}</span>
                                    <Badge variant={r.status === 'open' ? 'default' : 'outline'} data-testid={`round-status-${r.number}`}>
                                        {t(`sitting.round_status.${r.status}`)}
                                    </Badge>
                                </div>
                                <RoundResult round={r} />
                                {live && running && (
                                    <Can perm="contracts.write">
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {r.status === 'pending' && (
                                                <>
                                                    <Button size="sm" disabled={anyOpen} data-testid={`round-open-${r.number}`}
                                                        onClick={() => m.open.mutate(resPath(r.resolution_uid))}>{t('sitting.open')}</Button>
                                                    <Button size="sm" variant="outline" data-testid={`round-amend-${r.number}`}
                                                        onClick={() => { setAmendFor(r); setAmendText(r.text); setAmendSubstantial(false) }}>{t('sitting.amend')}</Button>
                                                </>
                                            )}
                                            {r.status === 'open' && (
                                                <>
                                                    <Button size="sm" variant="outline" data-testid={`round-clerk-${r.number}`}
                                                        onClick={() => { setClerkFor(r); setClerkVotes({}) }}>{t('sitting.clerk')}</Button>
                                                    <Button size="sm" data-testid={`round-close-${r.number}`}
                                                        onClick={() => m.closeVote.mutate(resPath(r.resolution_uid))}>{t('sitting.close_vote')}</Button>
                                                </>
                                            )}
                                        </div>
                                    </Can>
                                )}
                            </li>
                        ))}
                    </ol>
                    </div>
                    {sitting.questions.length > 0 && (
                        <div data-testid="sitting-questions" className="space-y-2">
                            <p className="font-medium text-sm">{t('sitting.questions')}</p>
                        <ul className="space-y-2 text-sm">
                            {sitting.questions.map((q) => (
                                <li key={q.uid} className="flex items-center justify-between gap-2">
                                    <span>{q.name} · {q.kind === 'hand' ? t('sitting.hand') : q.body}</span>
                                    {!q.answered_at && (
                                        <Button size="sm" variant="outline" data-testid={`question-answer-${q.uid}`}
                                            onClick={() => m.answer.mutate({ params: { path: { ...path, questionUid: q.uid! } } })}>{t('sitting.answered')}</Button>
                                    )}
                                </li>
                            ))}
                        </ul>
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: 'closing',
            title: t('sitting.step.closing'),
            status: status === 'closed' ? 'done' : !allDone || !votingStarted ? 'blocked' : 'todo',
            summary: sitting.signature
                ? t('sitting.signature_state', { signed: sitting.signature.signed ?? 0, total: sitting.signature.signers ?? 0 })
                : t('sitting.closing_hint_short'),
            content: (
                <div data-testid="sitting-closing" className="space-y-3 text-sm">
                    {sitting.signature ? (
                        <p data-testid="sitting-signature">
                            {t('sitting.signature_state', { signed: sitting.signature.signed ?? 0, total: sitting.signature.signers ?? 0 })}
                        </p>
                    ) : (
                        <p className="flex items-center gap-1 text-muted-foreground" data-testid="sitting-closing-hint">
                            {t('sitting.closing_hint_short')}
                            <HelpHint testId="sitting-closing-help">{t('sitting.closing_hint')}</HelpHint>
                        </p>
                    )}
                    {sitting.signature?.overdue && (
                        <Alert variant="warning" data-testid="sitting-signature-overdue">
                            <AlertDescription>{t('sitting.signature_overdue')}</AlertDescription>
                        </Alert>
                    )}
                    <Can perm="contracts.write">
                        <div className="flex flex-wrap gap-2">
                            {live && !sitting.signature && (
                                <Button size="sm" variant="outline" disabled={!allDone} data-testid="sitting-minutes"
                                    onClick={() => m.minutes.mutate({ params: { path } })}>{t('sitting.generate_minutes')}</Button>
                            )}
                            {live && sitting.signature && (
                                <Button size="sm" variant="outline" disabled={!signatureDone} data-testid="sitting-close"
                                    onClick={() => m.close.mutate({ params: { path } })}>{t('sitting.close')}</Button>
                            )}
                            <Button size="sm" variant="outline" data-testid="sitting-evidence"
                                onClick={() => void downloadEvidence(org, minutesUid)}>{t('sitting.evidence')}</Button>
                        </div>
                    </Can>
                    {status === 'closed' && (
                        <div data-testid="sitting-contestants">
                            <p className="font-medium">{t('sitting.contestants')}</p>
                            <ul className="list-disc pl-5">
                                {(contestants?.data ?? []).map((c) => (
                                    <li key={c.user_uid}>
                                        {c.name} · {(c.resolutions ?? []).map((r) => `${r.number} (${t(`vote.${r.vote}`, { defaultValue: r.vote })}, ${fmt(r.notified_at)})`).join(', ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            ),
        },
    ]
    // Follow the work: the attendance until the first vote opens, the votes
    // while some remain, the closing once they are all done.
    const auto =
        status === 'closed' || sitting.signature ? 'closing' : !votingStarted ? 'attendance' : anyOpen || nextPending ? 'rounds' : 'closing'
    const openStep = chosen === null ? auto : chosen

    return (
        <div className="space-y-4" data-testid="sitting-tab">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                    <CardTitle className="text-base">{t('sitting.title')}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={suspended ? 'warning' : 'outline'} data-testid="sitting-status">
                            {t(`sitting.status.${sitting.sitting?.status ?? 'running'}`)}
                        </Badge>
                        <Button variant="outline" size="sm" asChild data-testid="sitting-room-link">
                            <Link to="/$org/assemblies/$uid/room" params={{ org, uid: minutesUid }}>{t('sitting.room')}</Link>
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <ParticipationLine sitting={sitting} />
                    {suspended && (
                        <Alert variant="warning" data-testid="sitting-suspended">
                            <AlertTitle>{t('sitting.suspended_title')}</AlertTitle>
                            <AlertDescription>{sitting.sitting?.suspended_reason || t('sitting.suspended_body')}</AlertDescription>
                        </Alert>
                    )}
                    {live && (
                        <Can perm="contracts.write">
                            <div className="flex flex-wrap items-center gap-2">
                                {running ? (
                                    <>
                                        <Input className="h-8 max-w-xs" value={reason} onChange={(e) => setReason(e.target.value)}
                                            placeholder={t('sitting.suspend_reason')} data-testid="sitting-suspend-reason" />
                                        <Button size="sm" variant="outline" data-testid="sitting-suspend"
                                            onClick={() => m.suspend.mutate({ params: { path }, body: { reason } })}>{t('sitting.suspend')}</Button>
                                    </>
                                ) : suspended ? (
                                    <Button size="sm" data-testid="sitting-resume" onClick={() => m.resume.mutate({ params: { path } })}>{t('sitting.resume')}</Button>
                                ) : null}
                            </div>
                        </Can>
                    )}
                    {sitting.video_url && (
                        <p className="text-xs text-muted-foreground" data-testid="sitting-video">
                            {t('sitting.video')} <a className="underline" href={sitting.video_url} target="_blank" rel="noreferrer">{sitting.video_url}</a>
                        </p>
                    )}
                    {error && <p className="text-destructive" data-testid="sitting-error">{error}</p>}
                </CardContent>
            </Card>


            {live && (
                <Can perm="contracts.write">
                    {openRound ? (
                        <NextAction label={t('steps.next_action')} hint={t('sitting.next.close_vote', { number: openRound.number, title: openRound.title })} testId="sitting-next-action">
                            <Button disabled={!running} data-testid="sitting-next-close-vote"
                                onClick={() => m.closeVote.mutate(resPath(openRound.resolution_uid))}>{t('sitting.close_vote')}</Button>
                        </NextAction>
                    ) : nextPending && present.length > 0 ? (
                        <NextAction label={t('steps.next_action')} hint={t('sitting.next.open_vote', { number: nextPending.number, title: nextPending.title })} testId="sitting-next-action">
                            <Button disabled={!running} data-testid="sitting-next-open-vote"
                                onClick={() => m.open.mutate(resPath(nextPending.resolution_uid))}>{t('sitting.open')}</Button>
                        </NextAction>
                    ) : nextPending ? (
                        <NextAction label={t('steps.next_action')} hint={t('sitting.next.check_in')} testId="sitting-next-action">
                            <Button onClick={() => setChosen('attendance')} data-testid="sitting-next-check-in">{t('sitting.check_in')}</Button>
                        </NextAction>
                    ) : !sitting.signature ? (
                        <NextAction label={t('steps.next_action')} hint={t('sitting.next.minutes')} testId="sitting-next-action">
                            <Button disabled={!allDone} data-testid="sitting-next-minutes"
                                onClick={() => m.minutes.mutate({ params: { path } })}>{t('sitting.generate_minutes')}</Button>
                        </NextAction>
                    ) : (
                        <NextAction label={t('steps.next_action')} hint={signatureDone ? t('sitting.next.close') : t('sitting.next.signatures')} testId="sitting-next-action">
                            <Button disabled={!signatureDone} data-testid="sitting-next-close"
                                onClick={() => m.close.mutate({ params: { path } })}>{t('sitting.close')}</Button>
                        </NextAction>
                    )}
                </Can>
            )}

            <StepList steps={steps} open={openStep} onOpenChange={setChosen} testId="sitting-steps" />

            <Dialog open={!!checkInFor} onOpenChange={(o) => !o && setCheckInFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t('sitting.check_in')}</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">{t('sitting.sign_here')}</p>
                    <SignaturePad onChange={setSignature} />
                    <DialogFooter>
                        <Button data-testid="sitting-checkin-confirm" onClick={() => {
                            if (checkInFor) m.checkIn.mutate({ params: { path }, body: { stakeholder_uid: checkInFor, signature_png: signature ?? undefined } })
                            setCheckInFor(null)
                        }}>{t('sitting.check_in')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!clerkFor} onOpenChange={(o) => !o && setClerkFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t('sitting.clerk')}</DialogTitle></DialogHeader>
                    <label className="space-y-1 text-sm">
                        <span className="block text-xs text-muted-foreground">{t('sitting.read_back_by')}</span>
                        <select className="h-9 w-full rounded-md border border-input bg-background px-2" value={clerkScrutineer}
                            onChange={(e) => setClerkScrutineer(e.target.value)} data-testid="clerk-scrutineer">
                            <option value="">—</option>
                            {sitting.bureau.scrutineers.map((s) => <option key={s.user_uid} value={s.user_uid}>{s.name}</option>)}
                        </select>
                    </label>
                    <ul className="space-y-2 text-sm">
                        {present.map((a) => (
                            <li key={a.stakeholder_uid} className="flex items-center justify-between gap-2">
                                <span>{a.name}</span>
                                <select className="h-8 rounded-md border border-input bg-background px-2" value={clerkVotes[a.stakeholder_uid!] ?? ''}
                                    onChange={(e) => setClerkVotes({ ...clerkVotes, [a.stakeholder_uid!]: e.target.value })}
                                    data-testid={`clerk-vote-${a.user_uid}`}>
                                    <option value="">—</option>
                                    {['for', 'against', 'abstain'].map((v) => <option key={v} value={v}>{t(`vote.${v}`)}</option>)}
                                </select>
                            </li>
                        ))}
                    </ul>
                    <DialogFooter>
                        <Button data-testid="clerk-save" disabled={!clerkScrutineer || Object.values(clerkVotes).every((v) => !v)} onClick={() => {
                            if (!clerkFor) return
                            m.clerkVotes.mutate({
                                ...resPath(clerkFor.resolution_uid),
                                body: {
                                    scrutineer_user_uid: clerkScrutineer,
                                    votes: Object.entries(clerkVotes).filter(([, v]) => v).map(([stakeholder_uid, vote]) => ({ stakeholder_uid, vote: vote as 'for' })),
                                },
                            })
                            setClerkFor(null)
                        }}>{t('sitting.clerk_save')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!amendFor} onOpenChange={(o) => !o && setAmendFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t('sitting.amend')}</DialogTitle></DialogHeader>
                    <Textarea rows={5} value={amendText} onChange={(e) => setAmendText(e.target.value)} data-testid="amend-text" />
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox checked={amendSubstantial} onCheckedChange={(v) => setAmendSubstantial(v === true)} data-testid="amend-substantial" />
                        {t('sitting.amend_substantial')}
                    </label>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        {t('sitting.amend_hint_short')}
                        <HelpHint testId="sitting-amend-help">{t('sitting.amend_hint')}</HelpHint>
                    </p>
                    <DialogFooter>
                        <Button data-testid="amend-save" onClick={() => {
                            if (amendFor) m.amend.mutate({ ...resPath(amendFor.resolution_uid), body: { text: amendText, substantial: amendSubstantial } })
                            setAmendFor(null)
                        }}>{t('sitting.amend_save')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
